// useBranding — carrega a paleta do cliente ativo e aplica como CSS variables.
//
// Antes:
//   - Hook nunca era chamado (só exportado, ninguém invocava)
//   - Usava tenant.id (subdomain-based) — quebrava pro raz admin que troca
//     cliente pelo switcher
//   - hexToOklch era uma aproximação caseira que gerava cores estranhas
//     (ex: laranja virava marrom borrado)
//
// Agora:
//   - Plugado no AppLayout (roda em toda página protegida)
//   - Usa session.activeClientId (respeita switcher do raz admin)
//   - Aplica hex direto nos --brand-* + usa oklch(from #hex) pras vars
//     do Tailwind 4 (conversão nativa do browser)
//
// Os componentes shadcn/ui leem das vars do Tailwind 4 (--primary, --secondary,
// --accent). Sobrescrevemos essas + as brand-* extras pra estilo direto.

import { useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

// Vars que o hook pode sobrescrever — limpa todas antes de aplicar nova paleta
// pra raz admin trocando cliente não vazar cor antiga.
const VARS_TO_RESET = [
  // Brand semânticas (livre formato)
  "--brand-primary", "--brand-secondary", "--brand-accent",
  "--brand-bg", "--brand-surface", "--brand-text",
  // Shadcn / Tailwind 4 core
  "--primary", "--secondary", "--accent", "--ring",
  // SolarEdge theme (essas mexem em sidebar, gauges, bolinhas, etc — onde o
  // tema é visualmente percebido). NÃO mexemos em --solar-ink/--foreground
  // porque é o texto principal (precisa permanecer legível).
  "--solar-yellow", "--solar-amber", "--solar-orange",
  "--sidebar-primary", "--sidebar-ring",
];

export function useBranding() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 0;

  const { data: branding } = trpc.branding.get.useQuery(
    { clientId },
    { enabled: !!clientId, staleTime: 60_000 }
  );

  useEffect(() => {
    const root = document.documentElement;

    // 1. Reset — limpa qualquer override anterior (raz admin trocando cliente)
    // pra não vazar a paleta do cliente anterior pro novo.
    VARS_TO_RESET.forEach(v => root.style.removeProperty(v));

    if (!branding) return;

    // 2. PRIMARY → vira a cor "de marca" do cliente
    // Afeta: bg-primary, sidebar item ativo, gauge/anel, ring de focus.
    // NÃO mexe em --solar-ink/--foreground (texto principal, precisa legível).
    if (branding.primary) {
      root.style.setProperty("--brand-primary", branding.primary);
      root.style.setProperty("--primary",         branding.primary);  // shadcn
      root.style.setProperty("--sidebar-primary", branding.primary);  // item ativo do menu
      root.style.setProperty("--sidebar-ring",    branding.primary);
      root.style.setProperty("--ring",            branding.primary);
      root.style.setProperty("--solar-amber",     branding.primary);  // gauge/anel "44 APROV"
    }

    // 3. ACCENT → vira a cor de destaque (bolinhas, badges, highlights)
    // Afeta: --accent, --solar-yellow (bolinhas dos cards), --solar-orange.
    if (branding.accent) {
      root.style.setProperty("--brand-accent",   branding.accent);
      root.style.setProperty("--accent",         branding.accent);
      root.style.setProperty("--solar-yellow",   branding.accent);
      root.style.setProperty("--solar-orange",   branding.accent);
    }

    // 4. SECONDARY → cor neutra/dark (atalhos, segundo plano)
    if (branding.secondary) {
      root.style.setProperty("--brand-secondary", branding.secondary);
      root.style.setProperty("--secondary",       branding.secondary);
    }

    if (branding.bg)      root.style.setProperty("--brand-bg",      branding.bg);
    if (branding.surface) root.style.setProperty("--brand-surface", branding.surface);
    if (branding.text)    root.style.setProperty("--brand-text",    branding.text);

    // 3. Palette completa (chaves extras além do core)
    if (branding.palette && typeof branding.palette === "object") {
      const palette = branding.palette as Record<string, string>;
      Object.entries(palette).forEach(([key, value]) => {
        if (typeof value === "string") {
          root.style.setProperty(`--palette-${key}`, value);
        }
      });
    }
  }, [branding, clientId]);

  return branding;
}
